/**
 * Keep keyboard focus inside a modal panel while it is open (the phone menu
 * drawer, SellerMate W0.5): focus moves into the panel when it opens, Tab and
 * Shift+Tab wrap around the panel's own controls instead of walking into the
 * page behind the scrim, and focus goes back to the control that opened it
 * when it closes.
 *
 * Escape is not handled here - closing an overlay on Escape goes through the
 * shared dismiss hook until the overlay kit (components/overlay) lands.
 */
import { useEffect, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Where Tab should move focus, or null to let the browser move it normally.
 * `index` is the focused control's position among the panel's `count`
 * focusable controls, -1 when focus is not on one of them (the panel itself,
 * or somewhere behind it).
 */
export function trapTabTarget(count: number, index: number, shift: boolean): number | null {
  if (count <= 0) return null
  if (index < 0) return shift ? count - 1 : 0   // outside the controls: pull it back in
  if (!shift && index === count - 1) return 0   // Tab past the last: wrap to the first
  if (shift && index === 0) return count - 1    // Shift+Tab before the first: wrap to the last
  return null
}

/**
 * While `active`, trap Tab inside `panelRef` and focus the panel itself (give
 * it tabIndex={-1}) so the first Tab lands on its first control and nothing
 * is pre-armed. When it stops being active, focus returns to `returnFocusTo`,
 * else to whatever had focus before it opened.
 */
export function useFocusTrap(
  panelRef: RefObject<HTMLElement | null>,
  active: boolean,
  returnFocusTo?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!active) return
    const panel = panelRef.current
    if (!panel) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Resolved now, while the panel is open: the control to go back to is
    // mounted for the panel's whole life (the header hamburger).
    const back = returnFocusTo?.current ?? opener
    const controls = () => Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
    panel.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const list = controls()
      const at = document.activeElement instanceof HTMLElement ? list.indexOf(document.activeElement) : -1
      const next = trapTabTarget(list.length, at, e.shiftKey)
      if (next === null) return
      e.preventDefault()
      list[next].focus()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (back && back.isConnected) back.focus()
    }
  }, [active, panelRef, returnFocusTo])
}
